/* eslint-disable react/no-unused-prop-types */
import React, { Fragment, Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import momentPropTypes from 'react-moment-proptypes';
import { forbidExtraProps } from 'airbnb-prop-types';
import moment from 'moment';
import omit from 'lodash/omit';
import 'react-dates/initialize';
import { DayPickerRangeController } from 'react-dates';
import { US_LONG_DATE } from 'js/modules/constants';
import { removeClass, addClass } from 'js/modules/utils';
import {
  START_DATE,
  END_DATE,
  HORIZONTAL_ORIENTATION,
  VERTICAL_SCROLLABLE,
  ScrollableOrientationShape,
  isInclusivelyAfterDay,
} from 'js/modules/reactDatesUtilities';

const internals = {};

internals.errorMessage = {
  ERR100: 'Availability quotes may only be obtained for resort stays up to 21 nights. Please correct your request and try again.',
  ERR200: 'Air Availability cannot be selected for dates more than 330 days from today. Your air selection has been deleted.',
};

internals.CustomArrow = ({ orientation = 'next' }) => (
  <span className={`icon icon-button-arrow-${orientation}`} />
);

class DayPickerRangeControllerWrapper extends Component {

  constructor(props) {
    super(props);

    const { initialStartDate: startDate, initialEndDate: endDate, startFrom } = this.props;
    let currentMonth = moment();

    if(startDate && startDate.format('MM/YYYY') !== currentMonth.format('MM/YYYY')) {
      currentMonth = startDate.clone();
    }
    this.state = {
      focusedInput: null,
      startDate,
      endDate,
      errorMsg: '',
      isVisibleNavPrev: false,
      isVisibleNavNext: true,
      currentMonth,
      nextMonth: currentMonth.clone().add(1, 'M'),
      calendarFocused: false,
      invalidDates: false,
      ssvUS: [],
      ssvCA: [],
    };

    this.onDatesChange = this.onDatesChange.bind(this);
    this.onFocusChange = this.onFocusChange.bind(this);
    this.isOutsideRange = this.isOutsideRange.bind(this);
    this.onOutsideClick = this.onOutsideClick.bind(this);
    this.scrollToMonth = this.scrollToMonth.bind(this);
    this.getAbsoluteMonths = this.getAbsoluteMonths.bind(this);

    this.currentDate = moment();
    this.minStartFrom = startFrom ? moment(startFrom) : moment();

    // References for the OutSideClick
    this.startDateRef = React.createRef();
    this.endDateRef = React.createRef();
    this.datePickerRef = React.createRef();

    /**
      Business Rules
      - minimumNights: 3 days
      - minDate: 3 days layer
      - maxDate: 2 years later
      - maximumNights: 21 days
      - maxFlightDate: 330 days
    **/

    /* extraMonth is used to fix maxDate issue with calendar navigation SOBE-1392.
    Added an extra month to make the month after the maxDate a 'valid' month
    that users can navigate to, while keeping the maxDate as December, and blocking
    the right arrow when navigating to December.

    January days are greyed out using isOutsideRange.
    Using maxDate instead of extraMonth will block navigation to future months when
    selecting December dates, then navigating to a previouse year using the year dropdown.
    - Luis Booth */

    this.businessRules = {
      minimumNights: 1,
      // minDate: moment(this.currentDate, 'MM-DD').add(1, 'days'),
      // this is to set the minimum month the calendar will show, the dates enabled will be determined by minDate prop
      minCalendarDate: moment(this.currentDate, 'MM-DD').subtract(1, 'months'),
      minDate: startFrom ? moment(this.minStartFrom, 'MM-DD') : moment(this.currentDate, 'MM-DD').add(4, 'days'),
      maxDate: moment('01-01-2028', 'MM-DD-YYYY', true),
      extraMonth: moment('02-01-2028', 'MM-DD-YYYY', true),
      maximumNights: 21,
    };

    this.numberOfMonthsToShow = this.businessRules.maxDate.diff(this.businessRules.minDate, 'months') + 1;

    // Refs
    this.elHtml = document.querySelector('html');
    this.elBody = document.querySelector('body');

    this.initialVisibleMonth = () => this.businessRules.minDate;
  }

  componentDidMount() {
    const stateCopy = { ... this.state };
    const { startDate } = stateCopy;
    /**
     * 0 - Monday
     * 1 - Tuesday
     * 2 - Wednesday
     * 3 - Thursday
     * 4 - Friday
     * 5 - Saturday
     * 6 - Sunday
     */
    const SSVDaysUS = [1, 3, 6];
    const SSVDaysCA = [1, 2, 4, 6];

    if (startDate !== null) {
      this.checkNavsVisibility(startDate);
    }
    this.setState({ssvCA: SSVDaysCA, ssvUS: SSVDaysUS });
  }

  componentDidUpdate() {
    const {startFrom, rstCode} = this.props;

    this.initialVisibleMonth = () => startFrom && rstCode === 'btc' ? moment(this.minStartFrom, 'MM-DD') : moment(this.currentDate, 'MM-DD').add(4, 'days');
    this.businessRules.minDate = moment(this.currentDate, 'MM-DD').add(4, 'days');
  }

  noBodyScroll = (focusedInput) => {

    const noScroll = 'util-no-flow';

    if (focusedInput) {
      addClass(this.elHtml, noScroll);
      addClass(this.elBody, noScroll);
    } else {
      removeClass(this.elHtml, noScroll);
      removeClass(this.elBody, noScroll);
    }
  }

  onDatesChange({ startDate, endDate }) {
    const datesCloned = {
      startDate: startDate ? startDate.clone() : '',
      endDate: endDate ? endDate.clone() : '',
    };

    this.props.onChange(datesCloned);

    let invalidDates = true;

    if(startDate && endDate) {
      const nights = endDate.diff(startDate, 'days');

      if(nights <= 21) {
        invalidDates = nights < 3;
      } else if(nights > 21) {
        invalidDates = nights > 21;
      } else {
        invalidDates = false;
      }
    }

    this.setState({
      startDate,
      endDate,
      invalidDates
    });
    // save current month state so it shows the correct months when reopening even if the navigation wasn't triggered
    // ISSUE: SOBE-1272
    this.saveCalendarDates(startDate || endDate);
  }

  checkMaximumNights({ startDate, endDate }) {

    return (startDate && endDate) && (
      endDate.diff(startDate, 'days') > this.businessRules.maximumNights
    );
  }

  getAbsoluteMonths(momentDate) {
    const months = Number(momentDate.format('MM'));
    const years = Number(momentDate.format('YYYY'));

    return months + (years * 12);
  }

  scrollToMonth() {
    const startMonths = this.getAbsoluteMonths(this.businessRules.minDate);
    const endMonths = this.getAbsoluteMonths(this.state.startDate);
    const monthDifference = endMonths - startMonths + 1;
    const domNode = ReactDOM.findDOMNode(this);
    const mobileCalendar = domNode;
    const sepa = mobileCalendar.childNodes;

    setTimeout(() => {
      const months = Array.from(sepa[2].querySelectorAll('.CalendarMonth_caption'));
      const currentMonth = months[monthDifference];
      const offset = 410;
      const elementPosition = currentMonth.getBoundingClientRect().top;
      const offsetPosition = elementPosition - offset;
      const elementToScroll = mobileCalendar.querySelector('.DayPicker_transitionContainer__verticalScrollable');

      elementToScroll.scroll({
        top: offsetPosition,
      });
    }, 100);
  }

  onFocusChange(focusedInput) {

    if (focusedInput !== null && focusedInput === 'startDate') {
      this.state.startDate && this.isMobile && this.scrollToMonth();
    }

    const invalidDates = this.state.invalidDates;

    this.isMobile && this.noBodyScroll(focusedInput);

    this.setState({
      focusedInput: invalidDates ? 'endDate' : focusedInput
    });
  }

  onDatesFocusChange(focusedInput) {
    if (focusedInput !== null && focusedInput === 'startDate') {
      this.state.startDate && this.isMobile && this.scrollToMonth();
    }

    this.isMobile && this.noBodyScroll(focusedInput);

    const invalidDates = true;

    this.setState({
      focusedInput,
      invalidDates,
    });
  }

  focusThisInput(focusedInput) {

    return () => {
      // perform a check every if the calendar needs to render the left arrow depending on the current month shown.
      this.checkNavsVisibility(this.state.currentMonth.clone());
      this.onDatesFocusChange(focusedInput);
    };
  }

  isOutsideRange(day) {

    const isMinDate = !isInclusivelyAfterDay(day, this.businessRules.minDate);
    const isMaxDate = isInclusivelyAfterDay(day, this.businessRules.maxDate);

    return (isMinDate || isMaxDate);
  }

  validNumberOfNight(startDate, endDate) {
    if(startDate && endDate) {
      const nights = endDate.diff(startDate, 'days');

      if(nights > 21 || nights < 3) {
        return true;
      }
    }

    return false;
  }

  renderCalendarInfo() {
    const { isSandals = false } = window._app.brandOptions;
    const {startDate, endDate} = this.state;
    const minNightsWarning = 'Minimum 3-night stay is required to book online. For shorter stays call';
    const maxNightsWarning = 'Maximum 21-night stay when you book online. For longer stays call';
    const phoneNumber = isSandals ? 'tel:18887263257' : 'tel:18882322437';
    const maskNumber = isSandals ? '1-888-SANDALS' : '1-888-BEACHES';
    const isSSV = this.props.isSSV;
    const isMobile = this.isMobile;

    let warningMessage = '';
    let showWarningMessage = false;

    if(startDate && endDate) {
      const nights = endDate.diff(startDate, 'days');

      if(nights > 21) {
        warningMessage = maxNightsWarning;
      } else {
        if(nights < 3) {
          warningMessage = minNightsWarning;
        }
      }

      if(nights > 21 || nights < 3) {
        showWarningMessage = true;
      }
    }

    return () => (
      <Fragment>
        {showWarningMessage &&
          <div className="calendar-info-warning">
            <i className="warning icon-alert"/>
            <p className="warning-text">
              {warningMessage} <a href={phoneNumber}>{maskNumber}</a>.
            </p>
          </div>
        }
        {isSSV && !isMobile &&
          <div className="ssv-legend-container">
            <p className="ssv-legend">Best roundtrip flights to Saint Vincent</p>
          </div>
        }
      </Fragment>
    );
  }

  saveCalendarDates = (savedCurrentMonth) => {

    const { initialStartDate: startDate } = this.props;
    let currentMonth = null;

    if(savedCurrentMonth) {
      currentMonth = savedCurrentMonth;
    } else {
      if(this.state.currentMonth) {
        currentMonth = this.state.currentMonth.clone();
      } else {
        if(startDate && !currentMonth) {
          currentMonth = startDate.clone();
        }
      }
    }

    this.setState({
      currentMonth,
      nextMonth: currentMonth.clone().add(1, 'M'),
      calendarFocused: true
    }, () => {
      // force calendar update when years are distinct (do not set to !calendarFocused)
      this.setState({calendarFocused: false});
    });
  }

  onOutsideClick(e) {

    const selectedDate = this.state.startDate ? this.state.startDate.clone() : null;
    const invalidDates = false;

    if (e.target === this.endDateRef.current || e.target === this.startDateRef.current) {
      return;
    }

    this.onFocusChange(null);
    this.setState({
      isVisibleNavPrev: true,
      isVisibleNavNext: true,
      invalidDates
    });

    this.saveCalendarDates(selectedDate);
  }

  renderVacationLength(startDate, endDate) {

    const nights = endDate.diff(startDate, 'days');
    // const warning = (
    //   <span> please select 21 nights or less to book online. To book longer stays, please call <a href="tel:18887263257">1-888-SANDALS.</a> </span>
    // );

    // return (
    //   <p className={`form-feedback ${nights > 21 ? 'error is-invalid' : ''}`}>
    //     Vacation Length: {nights} night{nights > 1 ? 's' : ''}{nights > 21 ? warning : ''}
    //   </p>
    // );

    return (
      <p className={`form-feedback ${(nights > 21 || nights < 3) ? 'error is-invalid' : ''}`}>
        Vacation Length: {nights} night{nights > 1 ? 's' : ''}
      </p>
    );
  }

  checkNavsVisibility = (date) => {

    const dateYear = date.clone().year();
    const currentYear = moment().year();
    const maxDate = this.businessRules.maxDate.clone();

    if (currentYear <= dateYear && moment().isBefore(date)) {
      // prev must be visible
      this.setState({ isVisibleNavPrev: true });
    } else {
      // prev must not be visible
      this.setState({ isVisibleNavPrev: false });
    }

    // disables left arrow is current month is equal to the initial month shown in the calendar
    // to prevent navigating to past months
    if(date.isSame(this.businessRules.minDate, 'month')) {
      this.setState({ isVisibleNavPrev: false });
    }

    if (maxDate.clone().subtract(2, 'month').isAfter(date)) {
      // next must be visible
      this.setState({ isVisibleNavNext: true });
    } else {
      // next must be invisible
      this.setState({ isVisibleNavNext: false });
    }
  }

  onNavClick = (date) => {
    this.saveCalendarDates(date);
    this.checkNavsVisibility(date);
  }

  onClose = () => {

    const stateCopy = { ... this.state };
    const { startDate } = stateCopy;

    this.checkNavsVisibility(startDate);
  }

  onSelectChange = (month, onYearSelect) => e => {
    const today = new Date();
    const { value } = e.currentTarget;
    const valueAsNumber = Number(value);
    const navDate = `11/01/${valueAsNumber - 1}`;
    let navVisibilityDate = null;


    if (today.getFullYear() === valueAsNumber) {
      navVisibilityDate = moment().subtract(2, 'months');
      onYearSelect(navVisibilityDate, valueAsNumber - 1);
    } else {
      navVisibilityDate = moment(navDate, 'MM/DD/YYYY', true);
      onYearSelect(navVisibilityDate, valueAsNumber - 1);
    }

    this.checkNavsVisibility(navVisibilityDate);
  }

  renderMonthHeader = ({ month, onYearSelect }) => {
    // const maxDateYear = this.businessRules.maxDate.year();
    const minDateYear = this.businessRules.minDate.year();
    const options = [];

    for (let i = minDateYear; i <= 2027; i++) {
      options.push({
        value: i,
      });
    }

    return (
      <div>
        <span>
          {month.format('MMMM')}
        </span>
        { month.format('YYYY') <= 2027 ?
          <div className="input-wrap d-inline-block">
            <div className="select-wrap">
              <select
                className="input year-input"
                value={month.format('YYYY')}
                onChange={this.onSelectChange(month, onYearSelect)}
              >
                {
                  options.map(year => (
                    <option key={`yr-${year.value}`} {...year}>
                      {year.value}
                    </option>
                  ))
                }
              </select>
            </div>
          </div>
          :
          <span>
            {` ${month.format('YYYY')}`}
          </span>
        }
      </div>
    );
  }

  highlightedDay = (day) => {
    const currentDay = moment(day).day();
    const daysToHighlight = [... this.state.ssvUS]; // change this value with the validation by region
    const month = moment(day).month();
    const year = moment(day).year();

    if((month >= 3 && year === 2024) || (year > 2024)) {
      if(!daysToHighlight.includes(0)) {
        daysToHighlight.push(0);
      }
    }

    return daysToHighlight.some((highlight) => highlight === currentDay);
  }


  render() {
    const { focusedInput, startDate, endDate, calendarFocused, invalidDates } = this.state;
    const { isCheckInDate, isCheckOutDate, showWarningMessage } = this.props;

    let checkInDateClass = '';
    let checkOutDateClass = '';

    const props = omit(this.props, [
      'autoFocus',
      'initialStartDate',
      'initialEndDate',

      // Custom Props
      'onChange',
      'isCheckOutDate',
      'isCheckInDate',
      'showWarningMessage',
      'isSSV',
      'startFrom',
      'rstCode'
    ]);

    const startDateString = startDate ? startDate.format(US_LONG_DATE) : '';
    const endDateString = endDate ? endDate.format(US_LONG_DATE) : '';
    const isInvalidClassName = 'is-invalid';

    checkInDateClass = isCheckInDate ? '' : isInvalidClassName;
    checkOutDateClass = isCheckOutDate ? '' : isInvalidClassName;

    const isInvalidDate = showWarningMessage ? isInvalidClassName : '';
    const enterInvalidDates = this.validNumberOfNight(startDate, endDate);

    this.isMobile = window.innerWidth < 768;

    return (
      <Fragment>
        <div className={`row util-pr rct-dt-rng-pckr ${this.props.isSSV ? 'DayPicker-SSV' : ''} invalid-case-${enterInvalidDates ? 'yes' : 'no'}`}>
          <div className={`sm-12 md-6 lg-6 form-group ${focusedInput === START_DATE && 'active'}`}>
            <div className="util-pr">
              <input
                ref={this.startDateRef}
                className={`input ${checkInDateClass} ${isInvalidDate}`}
                type="text"
                name="start date"
                value={startDateString}
                onClick={this.focusThisInput(START_DATE)}
                placeholder="Check-In Date"
                readOnly />
              <i className="icon-calendar" onClick={this.focusThisInput(START_DATE)} />
            </div>
          </div>
          <div className={`sm-12 md-6 lg-6 form-group ${focusedInput === END_DATE && 'active'}`}>
            <div className="util-pr">
              <input
                ref={this.endDateRef}
                className={`input ${checkOutDateClass} ${isInvalidDate}`}
                type="text"
                name="end date"
                value={endDateString}
                onClick={this.focusThisInput(END_DATE)}
                placeholder="Check-Out Date"
                readOnly
              />
              <i className="icon-calendar" onClick={this.focusThisInput(END_DATE)} />
            </div>
          </div>

          {
            invalidDates &&
            <div ref={this.datePickerRef} className={`sm-12 lg-12 mobile-calendar ${focusedInput === START_DATE ? 'check-in' : 'check-out'}`} style={{}}>
              <div className="options visible-md-down">
                <div className="close util-txt-right">
                  Done
                </div>
                {this.props.isSSV ?
                  (<div className="ssv-legend-container">
                    <p className="ssv-legend ssv-mobile-text">Best roundtrip flights to Saint Vincent</p>
                  </div>) :
                  (
                    <div className="title util-txt-center">
                  Select <span className="blue">{focusedInput === START_DATE ? 'CHECK-IN' : 'CHECK-OUT'}</span> Date:
                    </div>
                  )
                }
              </div>
              <DayPickerRangeController
                {...props}
                onOutsideClick={this.onOutsideClick}
                onDatesChange={this.onDatesChange}
                onFocusChange={this.onFocusChange}
                focusedInput={focusedInput}
                // to force re-render
                isFocused={calendarFocused}

                initialVisibleMonth={(this.isMobile || (this.props.startFrom && !startDate)) ? this.initialVisibleMonth : null}
                startDate={startDate}
                endDate={endDate}
                minDate={this.businessRules.minCalendarDate}
                maxDate={this.businessRules.extraMonth}
                isDayHighlighted={ this.props.isSSV ? this.highlightedDay : undefined}
                // calendar presentation and interaction related props
                isOutsideRange={this.isOutsideRange}
                renderCalendarInfo={this.renderCalendarInfo()}
                numberOfMonths={this.isMobile ? this.numberOfMonthsToShow : 2}
                minimumNights={this.businessRules.minimumNights}
                orientation={this.isMobile ? VERTICAL_SCROLLABLE : HORIZONTAL_ORIENTATION}
                transitionDuration={0}
                withPortal={this.isMobile}
                noBorder={this.isMobile}
                hideKeyboardShortcutsPanel
                weekDayFormat="ddd"
                onPrevMonthClick={this.onNavClick}
                onNextMonthClick={this.onNavClick}
                navPrev={!(this.isMobile) && this.state.isVisibleNavPrev ? <i className="icon-prev" /> : <i className="" />}
                navNext={!(this.isMobile) && this.state.isVisibleNavNext ? <i className="icon-next" /> : <i className="" />}
                renderMonthElement={!(this.isMobile) ? this.renderMonthHeader : null}
              />
            </div>
          }
        </div>
        {
          (startDate && endDate) && this.renderVacationLength(startDate, endDate)
        }
        {(!isCheckInDate || !isCheckOutDate) && <p className="is-invalid">This field is required.</p>}
      </Fragment>
    );
  }
}

DayPickerRangeControllerWrapper.propTypes = forbidExtraProps({
  // example props for the demo
  initialStartDate: momentPropTypes.momentObj,
  initialEndDate: momentPropTypes.momentObj,
  startDateOffset: PropTypes.func,
  endDateOffset: PropTypes.func,

  keepOpenOnDateSelect: PropTypes.bool,
  isDayBlocked: PropTypes.func,
  isDayHighlighted: PropTypes.func,

  // DayPicker props
  enableOutsideDays: PropTypes.bool,
  numberOfMonths: PropTypes.number,
  orientation: ScrollableOrientationShape,
  verticalHeight: PropTypes.number,
  withPortal: PropTypes.bool,
  renderCalendarInfo: PropTypes.func,
  renderMonthElement: PropTypes.func,
  renderMonthText: PropTypes.func,

  navPrev: PropTypes.node,
  navNext: PropTypes.node,

  onPrevMonthClick: PropTypes.func,
  onNextMonthClick: PropTypes.func,
  renderCalendarDay: PropTypes.func,
  renderDayContents: PropTypes.func,

  // i18n
  monthFormat: PropTypes.string,

  isRTL: PropTypes.bool,

  // Custom Props
  onChange: PropTypes.func,
  isCheckOutDate: PropTypes.bool,
  isCheckInDate: PropTypes.bool,
  showWarningMessage: PropTypes.bool,
  isSSV: PropTypes.bool,
  startFrom: PropTypes.string,
  rstCode: PropTypes.string
});

DayPickerRangeControllerWrapper.defaultProps = {
  // example props for the demo
  initialStartDate: null,
  initialEndDate: null,
  startDateOffset: undefined,
  endDateOffset: undefined,

  // day presentation and interaction related props
  renderCalendarDay: undefined,
  renderDayContents: null,
  isDayBlocked: () => false,
  isDayHighlighted: () => false,
  enableOutsideDays: false,

  // calendar presentation and interaction related props
  orientation: HORIZONTAL_ORIENTATION,
  verticalHeight: undefined,
  withPortal: false,
  numberOfMonths: 2,
  keepOpenOnDateSelect: false,
  renderCalendarInfo: null,
  isRTL: false,
  renderMonthText: null,
  renderMonthElement: null,

  // navigation related props
  navPrev: null,
  navNext: null,
  onPrevMonthClick() { },
  onNextMonthClick() { },

  // internationalization
  monthFormat: 'MMMM YYYY',

  // Custom Props
  onChange() { },
  isCheckOutDate: true,
  isCheckInDate: true,
  showWarningMessage: false,
  isSSV: false,
  startFrom: '',
  rstCode: ''
};

export default DayPickerRangeControllerWrapper;



// WEBPACK FOOTER //
// ./js/components/step1/DateRangePicker.js